import { datadogLogs } from "@datadog/browser-logs";
import { GidType, toGid } from "@redotech/redo-model/shopify/gid-utils";
import { useCallback, useEffect, useRef } from "react";
import { StorefrontClient } from "../client/storefront";
import { getShopPayProductOnClickEvent } from "./checkout-buttons/on-click-logic";

// Selectors for various wallet payment buttons
const WALLET_SELECTORS = [
  "shop-pay-wallet-button", // Shopify Shop Pay
  "shopify-amazon-pay-button", // Shopify Amazon Pay
  "shopify-paypal-button", // Shopify PayPal
  "shopify-google-pay-button", // Shopify Google Pay
  "button.rebuy-cart__shop-pay-button", // Rebuy Parent Container that holds all Rebuy Buy Now Buttons
];

type AsyncEventHandler = (
  event: React.MouseEvent | React.TouchEvent,
) => Promise<void>;

type UseBuyNowCoverageProps = {
  shouldInterceptBuyNow?: boolean;
  onClickCoverage: AsyncEventHandler | null;
  storeFrontClient?: StorefrontClient;
  buyNowRedoProduct: string | null;
};

export const useBuyNowCoverage = ({
  shouldInterceptBuyNow = false,
  onClickCoverage,
  storeFrontClient,
  buyNowRedoProduct,
}: UseBuyNowCoverageProps) => {
  const onClickCoverageRef = useRef(onClickCoverage);
  const storeFrontClientRef = useRef(storeFrontClient);
  const buyNowRedoProductRef = useRef(buyNowRedoProduct);

  useEffect(() => {
    onClickCoverageRef.current = onClickCoverage;
  }, [onClickCoverage]);

  useEffect(() => {
    storeFrontClientRef.current = storeFrontClient;
  }, [storeFrontClient]);

  useEffect(() => {
    buyNowRedoProductRef.current = buyNowRedoProduct;
  }, [buyNowRedoProduct]);

  /**
   * Extracts variant ID and quantity from the product form
   */
  const extractProductFormData = (element: HTMLElement) => {
    const form = element.closest('form[action="/cart/add"]');
    if (!form) {
      datadogLogs.logger.info("Form with action='/cart/add' not found");
      return { variantId: 0, quantity: 1 };
    }

    const variantInput = form.querySelector(
      'input[name="id"]',
    ) as HTMLInputElement;
    const quantityInput = form.querySelector(
      'input[name="quantity"]',
    ) as HTMLInputElement;

    const variantId = variantInput ? parseInt(variantInput.value, 10) : 0;
    const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

    if (!variantId) {
      datadogLogs.logger.info("Valid variant ID not found within the form");
    }

    if (!quantity) {
      datadogLogs.logger.info("Valid quantity not found within the form");
    }

    return { variantId, quantity };
  };

  /**
   * Handles click events on wallet buttons
   */
  const handleWalletClick = useCallback(async (e: Event) => {
    const clickedElement = e.target as HTMLElement;
    const relevantButton = clickedElement.closest(
      WALLET_SELECTORS.join(","),
    ) as HTMLElement | null;

    if (!relevantButton) return;

    const eventToPass = e as unknown as React.MouseEvent | React.TouchEvent;

    // Check if this is a Shop Pay button on a product page
    const isShopPayButton = relevantButton.tagName === "SHOP-PAY-WALLET-BUTTON";
    const pageType = relevantButton.getAttribute("page-type");
    const isShopPayProductButton = isShopPayButton && pageType === "product";

    // Handle Shop Pay product page buttons specially
    if (
      isShopPayProductButton &&
      storeFrontClientRef.current &&
      buyNowRedoProductRef.current
    ) {
      const { variantId, quantity } = extractProductFormData(clickedElement);

      if (!variantId && !quantity) return;

      eventToPass.stopPropagation();

      const shopPayHandler = getShopPayProductOnClickEvent({
        storeFrontClient: storeFrontClientRef.current,
        productVariantId: toGid(GidType.PRODUCT_VARIANT, variantId),
        productVariantQuantity: quantity,
        redoProductVariantId: toGid(
          GidType.PRODUCT_VARIANT,
          buyNowRedoProductRef.current,
        ),
      });

      await shopPayHandler();
    }
    // Handle other wallet buttons with the provided coverage handler
    else if (onClickCoverageRef.current && !isShopPayProductButton) {
      if (isShopPayButton) eventToPass.stopPropagation();
      await onClickCoverageRef.current(eventToPass);
    }
  }, []);

  useEffect(() => {
    if (shouldInterceptBuyNow) {
      document.body.addEventListener("click", handleWalletClick, {
        capture: true,
      });
    }

    return () => {
      document.body.removeEventListener("click", handleWalletClick, {
        capture: true,
      });
    };
  }, [shouldInterceptBuyNow, handleWalletClick]);
};
